export const heroData = {
  title: "About the Company",
  subtitle: "Learn about the company behind AIRDOA — our philosophy and operations, dedicated to delivering safe and unforgettable aerial experiences.",
  image:
    "https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20floor%20to%20ceiling%20windows%20overlooking%20city%20skyline%20at%20dusk%2C%20warm%20ambient%20lighting%2C%20elegant%20corporate%20atmosphere%2C%20deep%20navy%20blue%20tones%20with%20gold%20accents%2C%20editorial%20architectural%20photography%2C%20no%20text%2C%20high%20quality&width=1920&height=700&seq=companyhero01&orientation=landscape",
};

export const leadData = {
  label: "ABOUT",
  title: "Why We Run AIRDOA",
  paragraphs: [
    "We believe the extraordinary wonder of helicopter sightseeing should be accessible to more people. The view from the sky is a truly special experience — one that simply cannot be replicated on the ground. And that experience has the power to make life's most precious moments — anniversaries, dates, proposals, sightseeing, gifts — shine even brighter.",
    "Yet information about helicopter tours is scattered across the internet, and the barrier to booking remains high. Where do you book? Is it safe? What should you wear? For first-timers, uncertainty never seems to end.",
    "AIRDOA was born to solve these challenges. By partnering with trusted operators and bringing everything together in one place, we make it easy to consult and book online — so anyone, even a complete beginner, can enjoy a special aerial experience with confidence.",
    "Safety first, always walking alongside each guest, and helping the views from the sky become beautiful memories for life. That is the reason AIRDOA exists.",
  ],
};

export const missionData = {
  label: "MISSION",
  title: "Mission",
  lead: "Creating special memories through aerial experiences",
  paragraphs: [
    "AIRDOA's mission is to deliver the extraordinary experience of helicopter sightseeing safely and at the highest quality, helping guests and the people they love create memories that last a lifetime.",
    "Panoramic views that cannot be seen from the ground, sunsets and night skylines visible only from the air — we strive every day to make these moments more accessible and more reassuring for everyone.",
  ],
};

export const visionData = {
  label: "VISION",
  title: "Vision",
  lead: "Making helicopter sightseeing an experience anyone can enjoy",
  paragraphs: [
    "Our vision is to create a world where helicopter sightseeing is not 'an experience only for a select few,' but 'a special experience everyone wants to try at least once.'",
    "Starting from Yokohama, we plan to expand to beautiful destinations across Japan, so that someday anyone can take to the skies from incredible locations nationwide. We aim to build a safe and reliable operating network.",
  ],
};

export const valuesData = {
  label: "VALUE",
  title: "Values",
  subtitle: "The three values we hold most dear as we build AIRDOA",
  items: [
    {
      icon: "ri-shield-check-line",
      title: "Uncompromising Safety",
      desc: "Safety is the foundation of everything. We partner exclusively with MLIT-certified operators, rigorously reviewing maintenance records, pilot credentials, and safety history. We are always conscious of our responsibility to our guests and never compromise on flight decisions.",
    },
    {
      icon: "ri-heart-3-line",
      title: "Building Trust",
      desc: "Trust cannot be built in an instant. Accurate information, honest responses, keeping our promises — trust is built through these small, consistent acts. AIRDOA walks alongside our guests and continuously creates an environment where they can choose experiences with confidence.",
    },
    {
      icon: "ri-sparkling-2-line",
      title: "Creating Moments of Magic",
      desc: "The value of an experience lives in the details. The texture of the gift wrap, the words in a booking confirmation email, the smile of a staff member on the day — we pay attention to every detail to make our guests' special days shine even brighter.",
    },
  ],
};

export const aboutData = {
  label: "COMPANY",
  title: "Company Profile",
  subtitle: "Basic information about the company that operates AIRDOA",
  items: [
    { label: "Company Name", value: "LUDOA Inc." },
    { label: "Head Office", value: "7F Konotorabiru, 1-1-14 Shimomeguro, Meguro-ku, Tokyo 153-0064" },
    { label: "Capital", value: "¥1,000,000" },
    { label: "Representative", value: "Kose Shibata, Representative Director" },
    { label: "Business", value: "" },
  ],
  business: {
    label: "Business Activities",
    description: "",
    items: [
      "Web marketing",
      "Website planning and development",
      "Consulting services",
      "Notion implementation and utilization consulting",
    ],
  },
};

export const messageData = {
  label: "MESSAGE",
  title: "Message from the CEO",
  name: "Kose Shibata",
  role: "Representative Director",
  greeting: "Thank you for visiting AIRDOA.",
  paragraphs: [
    "We launched this platform with the belief that the extraordinary experience of helicopter sightseeing should reach more people.",
    "'What does it look like from up there?' 'Is it safe?' 'How much does it cost?' — I came to realize that there are many people who carry this mixture of curiosity and uncertainty. Information is scattered everywhere, and it is hard to know where to start. The booking process feels daunting and it is hard to take that first step.",
    "AIRDOA is a platform designed to solve these challenges one by one. We work with trusted operators, organize accurate information, and make it easy to consult and book online — with dedicated staff ready to suggest the right plan for you.",
    "Anniversaries, dates, proposals, sightseeing, gifts. In life's most special moments, the view from the sky will surely become an unforgettable memory. With safety and trust as our foundation, we are committed to delivering a service that walks alongside each and every guest.",
    "Please experience the world from the sky together with the people most precious to you.",
  ],
};

export const teamData = {
  label: "TEAM",
  title: "Our Team",
  subtitle: "AIRDOA is operated by members who bring specialized expertise from their respective fields",
  members: [
    {
      role: "Representative Director",
      name: "Kose Shibata",
      desc: "Oversees business strategy, partner relationships, and brand direction.",
    },
    {
      role: "Operations Manager",
      name: "(Coming soon)",
      desc: "Responsible for day-to-day site operations, content management, and customer support.",
    },
    {
      role: "Safety Auditor",
      name: "(Coming soon)",
      desc: "Responsible for verifying safety standards of operators, reviewing maintenance records, and supporting flight cancellation decisions.",
    },
    {
      role: "Booking Concierge",
      name: "(Coming soon)",
      desc: "Handles booking inquiries, plan recommendations, and day-of guest assistance.",
    },
  ],
};

export const partnerData = {
  label: "PARTNER",
  title: "Our Relationships with Partners",
  subtitle: "Collaboration with carefully selected partner operators who support our guests' safety and delight",
  intro:
    "AIRDOA does not own or operate helicopters itself. Instead, we partner with MLIT-certified operators to deliver our services. This allows us to provide the best aircraft, flight routes, and pilots for each guest — ensuring a safe and premium experience.",
  items: [
    {
      icon: "ri-file-shield-line",
      title: "Rigorous Selection Standards",
      desc: "Partner operators are selected after a comprehensive review of their maintenance systems, pilot qualifications and flight hours, and safety track records. We do not partner with any operator that does not meet our standards.",
    },
    {
      icon: "ri-links-line",
      title: "Ongoing Quality Management",
      desc: "Even after partnering, we regularly review operators' maintenance status and safety management systems. Customer feedback is also shared to continuously improve service quality.",
    },
    {
      icon: "ri-customer-service-line",
      title: "Direct Guest Communication",
      desc: "AIRDOA serves as the point of contact for guests, handling everything from booking inquiries to day-of guidance. We coordinate with operators on behalf of our guests to minimize their effort.",
    },
  ],
};

export const finalCTA = {
  title: "Want to learn more\nabout AIRDOA?",
  subtitle: "Feel free to contact us if you have any questions about the company or our services",
  ctas: [
    { label: "Contact Us", href: "/contact", type: "primary" },
    { label: "View FAQ", href: "/faq", type: "ghost" },
  ],
};
